import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDocFromServer } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { safeStringify } from './utils';
import firebaseConfig from '../../firebase-applet-config.json';

const app = initializeApp(firebaseConfig);
console.log(`[FIREBASE FRONTEND] Initialized with Project ID: ${firebaseConfig.projectId}`);
console.log(`[FIREBASE FRONTEND] Database ID: ${firebaseConfig.firestoreDatabaseId}`);
export const auth = getAuth(app);
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId);
export const defaultDb = getFirestore(app);
export const storage = getStorage(app);

// Test connection on boot with timeout
async function testConnection() {
  const timeoutPromise = new Promise((_, reject) => 
    setTimeout(() => reject(new Error('Connection timed out')), 10000)
  );

  try {
    await Promise.race([
      getDocFromServer(doc(db, 'test', 'connection')),
      timeoutPromise
    ]);
    console.log('[Firestore] Connection verified.');
    window.dispatchEvent(new CustomEvent('firestoreConnectivity', { detail: { isOffline: false } }));
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes('offline') || msg.includes('reach') || msg.includes('timed out')) {
      console.warn("[Firestore] WARNING: System may be offline or backend unreachable. Check VITE environment variables if this persists.");
      window.dispatchEvent(new CustomEvent('firestoreConnectivity', { detail: { isOffline: true } }));
    }
  }
}
// testConnection();

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string;
    email?: string | null;
    emailVerified?: boolean;
    isAnonymous?: boolean;
    tenantId?: string | null;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const message = error instanceof Error ? error.message : String(error);
  
  // Update offline state if relevant
  if (message.includes('offline') || message.includes('reach')) {
    window.dispatchEvent(new CustomEvent('firestoreConnectivity', { detail: { isOffline: true } }));
  }

  const authInfo = {
    userId: auth.currentUser?.uid,
    email: auth.currentUser?.email,
    emailVerified: auth.currentUser?.emailVerified,
    isAnonymous: auth.currentUser?.isAnonymous,
    tenantId: auth.currentUser?.tenantId,
    providerInfo: (auth.currentUser?.providerData || []).map(provider => ({
      providerId: provider.providerId,
      displayName: provider.displayName,
      email: provider.email,
      photoUrl: provider.photoURL
    }))
  };

  const errInfo: FirestoreErrorInfo = {
    error: message,
    authInfo,
    operationType,
    path
  };

  const serialized = safeStringify(errInfo);
  
  // Detailed log as requested
  console.group('%c[Firestore Security Investigation]', 'background: #222; color: #ff5555; font-weight: bold; padding: 4px;');
  console.log('Path:', path);
  console.log('Collection:', path?.split('/')[0] || 'unknown');
  console.log('Operation:', operationType);
  console.log('Auth User ID:', authInfo.userId || 'NOT_AUTHENTICATED');
  console.log('User Email:', authInfo.email || 'N/A');
  console.log('CurrentUser Object Exists:', !!auth.currentUser);
  
  // Rule analysis hint
  if (message.includes('permission') || message.includes('insufficient')) {
    console.error('DENIAL DETECTED: Likely a security rule violation.');
    const collection = path?.split('/')[0];
    if (collection === 'courses') {
      console.info('Tip: Courses rules check tutorId matches request.auth.uid and visibility constraints.');
    } else if (collection === 'jobs') {
      console.info('Tip: Jobs rules check userId matches request.auth.uid.');
    } else if (collection === 'enrollments') {
      console.info('Tip: Enrollments rules check userId matches auth and course visibility.');
    }
  }

  console.groupEnd();

  throw new Error(serialized);
}
